import React, { useCallback, useEffect, useState } from 'react';
import {
  AppCard,
  AppCheckbox,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { Tab, Tabs } from 'react-bootstrap';
import { axiosPath } from '../../../../../routing/helpers/router';
import useIsMounted from '../../../../components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import ObligationManageCard from './ObligationManageCard';
import Translator from '../../../../components/helpers/Translator';

export default function ObligationResourceTabEditMode({
  elementID,
  setEditMode,
}) {
  const { moduleBridgeHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const resourceObligations = useSelector(
    (s) => s.currentPage?.resourceObligationTab?.[elementID]
  );
  const updateResourceObligationTab = useSelector(
    (s) => s.currentPage?.updateResourceObligationTab
  );
  const [obligationsToRemove, setObligationsToRemove] = useState([]);
  const [obligationsToAdd, setObligationsToAdd] = useState([]);
  const [showConformModal, setShowConformModal] = useState(false);
  const [confirmChanges, setConfirmChanges] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('existing');
  const [internalObligations, setInternalObligations] = useState();

  // > LOAD INTERNAL OBLIGATIONS
  const loadInternalObligations = useCallback(async () => {
    return axios
      .post(
        axiosPath(
          "api_r_user_module_resourceManager_resourceObligation_update_getInternals"
        ),
        {
          moduleBridgeHex,
          existingInternalObligations: resourceObligations?.filter(
            (ro) => ro.obligation.isInternal === true
          )?.map,
        }
      )
      .then(({ data }) => {
        if (isMounted.current) {
          setInternalObligations(data);
          setLoading(false);
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, [moduleBridgeHex, elementID, resourceObligations]);

  useEffect(() => {
    loadInternalObligations();
  }, []);
  // < LOAD INTERNAL OBLIGATIONS

  // > UPDATE OBLIGATIONS
  const updateObligations = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_resourceObligation_update"
          ),
          {
            moduleBridgeHex,
            resourceID: elementID,
            obligationsToRemove,
            obligationsToAdd,
          }
        )
        .then(() => {
          if (isMounted.current) {
            setShowConformModal(false);
            setEditMode(false);
            dispatch(currentPageAction.change("updateCard", elementID, true));
            dispatch(
              currentPageAction.change(
                "updateResourceObligationTab",
                elementID,
                true
              )
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, elementID, obligationsToRemove, obligationsToAdd]
  );

  useEffect(() => {
    if (updateResourceObligationTab === elementID) updateObligations();
  }, [updateResourceObligationTab]);
  // < UPDATE OBLIGATIONS

  if (loading) return <AppNoInfo />;

  return (
    <>
      <div className="p-0 mt-2">
        <div className="float-right">
          <span
            className="btn btn-xs btn-outline-danger mb-2 mr-2"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.cancel"
              />
            )}
            onClick={() => setEditMode(false)}
          >
            <AppRemixIcon icon="close" />
          </span>
          <span
            className="btn btn-xs btn-outline-success mb-2"
            title={ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.save.changes"
              />
            )}
            onClick={() => setShowConformModal(true)}
          >
            <AppRemixIcon icon="save" />
          </span>
          <AppModal
            show={showConformModal}
            title={
              <Translator
                file="module-resourceManager"
                name="resource.confirm.changes"
              />
            }
            onClose={() => setShowConformModal(false)}
          >
            <p>
              <Translator
                file="module-resourceManager"
                name="resource.sure.want.update.obligations"
              />
              <br />
              <Translator
                file="module-resourceManager"
                name="resource.obligations.eliminated.cannot.recovered"
              />
              .
            </p>
            <AppCheckbox
              value={confirmChanges}
              onChange={() => setConfirmChanges(true)}
            >
              <Translator
                file="module-resourceManager"
                name="resource.confirm.want.confirm.changes"
              />
            </AppCheckbox>
            <div className="row">
              <div className="col-lg-12">
                <div className="d-flex justify-content-between">
                  <span className="btn btn-xs btn-outline-light mb-2">
                    <Translator
                      file="module-resourceManager"
                      name="resource.cancel"
                    />
                  </span>
                  <span
                    className={`btn btn-xs btn-outline-danger mr-2 mb-2 ${
                      !confirmChanges ? 'disabled' : ''
                    }`}
                    onClick={() => confirmChanges && updateObligations()}
                  >
                    <Translator
                      file="module-resourceManager"
                      name="resource.accept"
                    />
                  </span>
                </div>
              </div>
            </div>
          </AppModal>
        </div>
        <Tabs defaultActiveKey={activeTab} onSelect={(v) => setActiveTab(v)}>
          <Tab
            eventKey="existing"
            title={
              <Translator
                file="module-resourceManager"
                name="resource.current.obligations"
              />
            }
            className="p-3 border border-top-0 bg-white"
          >
            {activeTab === 'existing' && (
              <>
                <div className="d-flex justify-content-between">
                  <div className="alert alert-info">
                    <Translator
                      file="module-resourceManager"
                      name="resource.obligations.delete.pending.configuration"
                      isHtml
                    />
                  </div>
                </div>
                <div>
                  <span
                    className="btn btn-xs btn-outline-light mr-2 mb-2"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.select.all"
                      />
                    )}
                    onClick={() =>
                      setObligationsToRemove(
                        resourceObligations
                          ?.filter((ro) => ro.state === -1)
                          ?.map((ro) => ro.id)
                      )
                    }
                  >
                    <AppRemixIcon icon="checkbox-multiple" />
                  </span>
                  <span
                    className="btn btn-xs btn-outline-light mb-2"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.deselect.all"
                      />
                    )}
                    onClick={() => setObligationsToRemove([])}
                  >
                    <AppRemixIcon icon="checkbox-multiple-blank" />
                  </span>
                </div>
                <ul className="list-group">
                  {resourceObligations !== undefined &&
                    resourceObligations?.map((ro) => (
                      <ObligationManageCard
                        key={ro?.obligation?.id}
                        resourceObligation={ro}
                        obligationsToRemove={obligationsToRemove}
                        setObligationsToRemove={setObligationsToRemove}
                      />
                    ))}
                </ul>
              </>
            )}
          </Tab>
          <Tab
            eventKey="internals"
            title={
              <Translator
                file="module-resourceManager"
                name="resource.internal.obligations"
              />
            }
            className="p-3 border border-top-0 bg-white"
          >
            {activeTab === 'internals' && (
              <>
                <div className="d-flex justify-content-between">
                  <div className="alert alert-info">
                    <Translator
                      file="module-resourceManager"
                      name="resource.not.currently.exist.added.already.ignored"
                    />
                  </div>
                </div>
                <div>
                  <span
                    className="btn btn-xs btn-outline-light mr-2 mb-2"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.select.all"
                      />
                    )}
                    onClick={() =>
                      setObligationsToAdd(internalObligations?.map((o) => o.id))
                    }
                  >
                    <AppRemixIcon icon="checkbox-multiple" />
                  </span>
                  <span
                    className="btn btn-xs btn-outline-light mb-2"
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.deselect.all"
                      />
                    )}
                    onClick={() => setObligationsToAdd([])}
                  >
                    <AppRemixIcon icon="checkbox-multiple-blank" />
                  </span>
                </div>
                <ul className="list-group">
                  {internalObligations?.map((o) => (
                    <ObligationManageCard
                      key={o?.id}
                      obligation={o}
                      setObligationsToAdd={setObligationsToAdd}
                      obligationsToAdd={obligationsToAdd}
                      add
                    />
                  ))}
                </ul>
              </>
            )}
          </Tab>
        </Tabs>
      </div>
    </>
  );
}
